import React, { createContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_URL as API } from '@/utils/apiConfig';

/**
 * PresenceContext — tracks whether the app is foregrounded.
 *
 * While visible: POST /api/presence/heartbeat every 30s.
 * On visibilitychange→hidden / pagehide: fire an explicit
 *   /api/presence/away beacon (via navigator.sendBeacon which is reliable
 *   during the page-freeze window on mobile).
 *
 * The backend uses this to decide whether to deliver push notifications
 * for events the user is about to see (e.g., chat reply that finished
 * generating server-side after the SSE connection died).
 */

// Must match `PRESENCE_HEARTBEAT_SECONDS` in
// /app/backend/app/services/presence_service.py
const HEARTBEAT_INTERVAL_MS = 30 * 1000;

const PresenceContext = createContext(null);

export function PresenceProvider({ children }) {
  const location = useLocation();
  const intervalRef = useRef(null);
  const routeRef = useRef(location.pathname);
  const haltedRef = useRef(false);

  useEffect(() => {
    routeRef.current = location.pathname;
  }, [location.pathname]);

  const sendHeartbeat = useCallback(async () => {
    if (haltedRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    try {
      await axios.post(
        `${API}/presence/heartbeat`,
        { route: routeRef.current },
        { withCredentials: true, timeout: 8000 },
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        haltedRef.current = true;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }
  }, []);

  // Fire an "away" beacon synchronously on backgrounding. Uses
  // navigator.sendBeacon which is guaranteed to complete even as the page
  // is being frozen/unloaded.
  const sendAway = useCallback(() => {
    if (haltedRef.current) return;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        // sendBeacon doesn't send cookies on cross-origin — but our API is
        // same-origin in production (REACT_APP_BACKEND_URL points at the
        // same host), so cookies go through.
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        navigator.sendBeacon(`${API}/presence/away`, blob);
      } else {
        // Fallback: fire-and-forget axios call with keepalive
        fetch(`${API}/presence/away`, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        }).catch(() => {});
      }
    } catch {
      // Best-effort; never throw from unload path.
    }
  }, []);

  useEffect(() => {
    const start = () => {
      if (haltedRef.current) return;
      sendHeartbeat();
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
        sendAway();
      }
    };

    const handlePageHide = () => {
      stop();
      sendAway();
    };

    if (document.visibilityState === 'visible') {
      start();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('focus', start);
    window.addEventListener('blur', stop);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('focus', start);
      window.removeEventListener('blur', stop);
    };
  }, [sendHeartbeat, sendAway]);

  return (
    <PresenceContext.Provider value={null}>
      {children}
    </PresenceContext.Provider>
  );
}

export default PresenceContext;
